import { useMemo, useCallback } from 'react';
import { useSignalkDashboardData } from './useSignalkDashboardData';
import { resolveWidgetSignalkValue } from '../utils/signalkWidgetData';
import {
  resolveLiveDataPath,
  resolveSignalKNode,
  normalizeSignalkScalar,
} from '../utils/signalKValueFormat';
import { formatExplorerDisplayString, formatExplorerReading } from '../utils/signalkPathUtils';
import { LIVE_BOAT_ROLES } from '../config/liveBoatRoles';
import { buildSignalkFlatMap, summarizePropulsionAlarms } from '../utils/propulsionAlarmUtils';

function isPresentRaw(rawValue) {
  const scalar = normalizeSignalkScalar(rawValue);
  if (scalar === null || scalar === undefined) return false;
  if (scalar === '--') return false;
  if (typeof scalar === 'string' && scalar.trim() === '') return false;
  return true;
}

function readRawNumeric(path, { liveData, signalKFlat, signalKData, demoData }) {
  const combinedFlat = { ...signalKFlat, ...liveData };
  const live = resolveLiveDataPath(combinedFlat, path);
  if (live?.entry && isPresentRaw(live.entry.value)) {
    return normalizeSignalkScalar(live.entry.value);
  }
  const db = resolveSignalKNode(signalKData, path);
  if (db && isPresentRaw(db.rawValue)) {
    return normalizeSignalkScalar(db.rawValue);
  }
  const demo = resolveSignalKNode(demoData, path);
  if (demo && isPresentRaw(demo.rawValue)) {
    return normalizeSignalkScalar(demo.rawValue);
  }
  return null;
}

function readRolePath(path, ctx) {
  if (!path) return null;
  const rawValue = readRawNumeric(path, ctx);
  if (rawValue === null || rawValue === undefined) return null;

  const resolved = resolveWidgetSignalkValue({
    dataPath: path,
    widgetConfig: {},
    liveData: ctx.liveData,
    wsConnected: ctx.wsConnected,
    vesselStreamConnected: ctx.vesselStreamConnected,
    databaseData: ctx.signalKData,
    databaseFlat: ctx.signalKFlat,
    demoData: ctx.demoData,
  });
  if (!resolved || resolved.value === null || resolved.value === undefined || resolved.value === '--') {
    return null;
  }

  return {
    raw: rawValue,
    units: resolved.unit || '',
    timestamp: resolved.lastUpdate,
    sourceType: resolved.sourceType,
    formatted: formatExplorerDisplayString(path, rawValue, resolved.unit || ''),
    reading: formatExplorerReading(path, rawValue, resolved.unit || ''),
    path,
  };
}

function parsePowerW(entry) {
  if (!entry) return 0;
  const n = typeof entry.raw === 'number' ? entry.raw : parseFloat(entry.raw);
  return Number.isFinite(n) ? n : 0;
}

function parseSogKnots(sogEntry) {
  if (!sogEntry) return 0;
  const n = typeof sogEntry.raw === 'number' ? sogEntry.raw : parseFloat(sogEntry.raw);
  if (!Number.isFinite(n)) return 0;
  // SignalK SOG usually m/s
  return n < 8 ? n * 1.94384 : n;
}

function hasEngineData(roleData) {
  if (!roleData) return false;
  const rpm = roleData.rpm;
  if (!rpm) return false;
  const n = typeof rpm.raw === 'number' ? rpm.raw : parseFloat(rpm.raw);
  if (!Number.isFinite(n)) return false;
  const rpmEstimate = n < 15 ? n * 60 : n;
  return rpmEstimate > 200;
}

/**
 * Resolve all Live Boat roles from merged SignalK state.
 */
export function useLiveBoatData(vesselId) {
  const {
    signalKData,
    signalKFlat,
    liveData,
    demoData,
    wsConnected,
    vesselStreamConnected,
    vesselId: effectiveVesselId,
    loadSignalKData,
  } = useSignalkDashboardData(vesselId, { enableWebSocket: true });

  const ctx = useMemo(
    () => ({ liveData, wsConnected, vesselStreamConnected, signalKData, signalKFlat, demoData }),
    [liveData, wsConnected, vesselStreamConnected, signalKData, signalKFlat, demoData]
  );

  const roles = useMemo(() => {
    const out = {};
    Object.entries(LIVE_BOAT_ROLES).forEach(([roleId, def]) => {
      const fields = {};
      Object.entries(def.paths).forEach(([key, path]) => {
        fields[key] = readRolePath(path, ctx);
      });
      out[roleId] = { ...def, id: roleId, fields };
    });
    return out;
  }, [ctx]);

  const scene = useMemo(() => {
    const sog = parseSogKnots(roles.navigation?.fields?.sog);
    const shoreW = Math.abs(parsePowerW(roles.shore?.fields?.power));
    const solarW = parsePowerW(roles.solar?.fields?.panelPower);
    const acSource = String(roles.shore?.fields?.source?.raw || '').toLowerCase();

    if (sog >= 1) return { id: 'underway', label: 'Underway' };
    if (solarW > 15) return { id: 'solar', label: 'Solar charging' };
    if (shoreW > 5 || acSource.includes('shore')) return { id: 'shore', label: 'On shore power' };
    if (sog < 0.3) return { id: 'at_dock', label: 'At dock' };
    return { id: 'idle', label: 'Idle' };
  }, [roles]);

  const modes = useMemo(
    () => ({
      inverter: roles.inverter?.fields?.mode?.raw ?? roles.inverter?.fields?.chargerMode?.raw ?? null,
      solar: roles.solar?.fields?.controllerMode?.raw ?? null,
    }),
    [roles]
  );

  const showEngines = useMemo(
    () => hasEngineData(roles.enginePort) || hasEngineData(roles.engineStarboard),
    [roles]
  );

  const houseSoc = useMemo(() => {
    const soc = roles.houseBattery?.fields?.soc;
    if (!soc) return null;
    const n = typeof soc.raw === 'number' ? soc.raw : parseFloat(soc.raw);
    if (!Number.isFinite(n)) return null;
    return n <= 1.05 ? n * 100 : n;
  }, [roles]);

  const engineAlarms = useMemo(() => {
    const flat = buildSignalkFlatMap(ctx);
    return summarizePropulsionAlarms(flat);
  }, [ctx]);

  const showEngineZone = useMemo(
    () => showEngines || engineAlarms.pathCount > 0,
    [showEngines, engineAlarms.pathCount]
  );

  const refresh = useCallback(() => {
    if (effectiveVesselId) loadSignalKData(effectiveVesselId);
  }, [loadSignalKData, effectiveVesselId]);

  return {
    roles,
    scene,
    modes,
    houseSoc,
    showEngines,
    showEngineZone,
    engineAlarms,
    wsConnected,
    vesselStreamConnected,
    vesselId: effectiveVesselId,
    refresh,
  };
}
